import { useState, type FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { useOutletContext } from "react-router-dom";

import { type IncidentReport } from "components/CriticalIncident/interfaces";
import { useThrottledSectionUpdate } from '../../hooks/useThrottledSectionUpdate';
import { useIncidentReportContext } from "../../IncidentReportContextProvider";
import { FormControlWrapper } from "components/Shared";
import { ComboBox } from "components/Shared/Inputs";
import { confirm } from "components/Shared/ConfirmDialog";
import { ChooseYesNo } from "components/CriticalIncident/components/ChooseYesNo";
import { ParticipantAccordion } from "./components/ParticipantAccordion";
import { EMPTY_PARTICIPANT, PARTICIPANT_NUMBER_OPTIONS } from "./IndividualSupportSession.constants";

import "./IndividualSupportSession.scss";

export const IndividualSupportSession: FunctionComponent = () => {
  const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
  const { values, touched, errors, setFieldValue } = useFormikContext<IncidentReport>();
  const { isReadonly = false } = useOutletContext<{ isReadonly: boolean }>();
  const { isAU } = useIncidentReportContext();
  const throttledUpdateReport = useThrottledSectionUpdate('individualSupportSession', values.individualSupportSession);
  const [expandedParticipantIndex, setExpandedParticipantIndex] = useState<number | false>(-1);
  const individualSupportSessionParticipants = values.individualSupportSession?.participants ?? [];

  const handleSessionsChange = async (wereThereIndividualSupportSessions: boolean) => {
    if (!wereThereIndividualSupportSessions && values.individualSupportSession?.wereThereIndividualSupportSessions) {
      const confirmed = await confirm({
        title: t('clear_participants_confirm_title'),
        message: t('clear_participants_confirm_text'),
        confirmLabel: t('yes_remove'),
      });
      if (!confirmed) return;
    }

    const participants = wereThereIndividualSupportSessions
      ? (individualSupportSessionParticipants.length ? individualSupportSessionParticipants : [{ ...EMPTY_PARTICIPANT }])
      : [];
    const noOfParticipants = wereThereIndividualSupportSessions ? (values.individualSupportSession?.noOfParticipants || 1) : 0;

    setFieldValue('individualSupportSession.wereThereIndividualSupportSessions', wereThereIndividualSupportSessions);
    setFieldValue('individualSupportSession.noOfParticipants', noOfParticipants);
    setFieldValue('individualSupportSession.participants', participants);
    throttledUpdateReport({ wereThereIndividualSupportSessions, noOfParticipants: noOfParticipants, participants });
  };

  const handleNumberOfParticipantsChange = (noOfParticipants: number) => {
    const participantsToAdd = Math.max(0, noOfParticipants - individualSupportSessionParticipants.length);
    const participants = [...individualSupportSessionParticipants, ...Array.from({ length: participantsToAdd }, () => ({ ...EMPTY_PARTICIPANT }))];

    setFieldValue('individualSupportSession.noOfParticipants', noOfParticipants);
    setFieldValue('individualSupportSession.participants', participants);
    throttledUpdateReport({ noOfParticipants: noOfParticipants, participants });
  };

  const handleRemoveParticipant = (index: number) => {
    const participants = individualSupportSessionParticipants.filter((_, i) => i !== index);
    const noOfParticipants = participants.length;

    setFieldValue('individualSupportSession.participants', participants);
    setFieldValue('individualSupportSession.noOfParticipants', noOfParticipants);
    throttledUpdateReport({ participants, noOfParticipants: noOfParticipants });
  };

  const patchParticipant = (index: number, partial: Partial<typeof individualSupportSessionParticipants[number]>) => {
    const participants = individualSupportSessionParticipants.map((participant, i) => i === index ? { ...participant, ...partial } : participant);
    throttledUpdateReport({ participants });
  };

  return translationsReady && (
    <div className="individual-support-session">
      <h2>{t('individualSupportSession')}</h2>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            type="radio.yesno"
            label={`${t('were_there_individual_support_sessions')}*`}
            name="individualSupportSession.wereThereIndividualSupportSessions"
            fullWidth
            error={touched.individualSupportSession?.wereThereIndividualSupportSessions && Boolean(errors?.individualSupportSession?.wereThereIndividualSupportSessions)}
            helperText={touched.individualSupportSession?.wereThereIndividualSupportSessions ? t(errors?.individualSupportSession?.wereThereIndividualSupportSessions || ' ') : ''}
          >
            <ChooseYesNo
              label={`${t('were_there_individual_support_sessions')}*`}
              name="individualSupportSession.wereThereIndividualSupportSessions"
              value={values.individualSupportSession?.wereThereIndividualSupportSessions}
              onChange={(value) => handleSessionsChange(value as boolean)}
            />
          </FormControlWrapper>
        </Grid>

        {values.individualSupportSession?.wereThereIndividualSupportSessions && (
          <>
            <Grid item sm={12}>
              <FormControlWrapper
                isReadonly={isReadonly}
                type="select"
                fullWidth
                label={t('number_of_participants')}
                name="individualSupportSession.noOfParticipants"
                error={touched.individualSupportSession?.noOfParticipants && Boolean(errors?.individualSupportSession?.noOfParticipants)}
                helperText={touched.individualSupportSession?.noOfParticipants && t(errors?.individualSupportSession?.noOfParticipants || ' ')}
                options={PARTICIPANT_NUMBER_OPTIONS.map(n => ({ label: `${n}`, value: `${n}` }))}
              >
                <ComboBox
                  name="individualSupportSession.noOfParticipants"
                  required
                  options={PARTICIPANT_NUMBER_OPTIONS}
                  getOptionLabel={(option) => `${option}`}
                  isOptionEqualToValue={(option, value) => option === value}
                  getOptionDisabled={(option) =>
                    option <= individualSupportSessionParticipants.length &&
                    option !== values.individualSupportSession?.noOfParticipants
                  }
                  disableClearable
                  value={values.individualSupportSession?.noOfParticipants}
                  onChange={(_event, newValue) => newValue !== null && handleNumberOfParticipantsChange(newValue)}
                />
              </FormControlWrapper>
            </Grid>

            {individualSupportSessionParticipants.map((participant, index) => (
              <Grid item sm={12} key={index}>
                <ParticipantAccordion
                  index={index}
                  participant={participant}
                  isReadonly={isReadonly}
                  isAU={isAU}
                  removeAction={() => handleRemoveParticipant(index)}
                  removeHidden={individualSupportSessionParticipants.length <= 1}
                  patchParticipant={(partial) => patchParticipant(index, partial)}
                  expanded={expandedParticipantIndex === index}
                  onToggle={(isExpanded) => setExpandedParticipantIndex(isExpanded ? index : false)}
                />
              </Grid>
            ))}
          </>
        )}
      </Grid>
    </div>
  );
};
